<template>
  <v-data-table
    @input="updateSelected"
    :value="currentSelected"
    :headers="headers"
    :items="items"
    :hide-actions="hideActions"
    :sort-icon="sortIcon"
    :no-data-text="noDataText"
    item-key="id"
    class="financial-table"
  >
    <template #headerCell="props">
      <v-checkbox
        v-if="props.header.value === 'selected'"
        :value="allSelected"
        primary
        hide-details
        @click.stop="toggleAll"
      >
      </v-checkbox>
      <span> {{ props.header.text }}</span>
    </template>
    <!-- Slot "row" para sobrescrever qualquer linha -->
    <template #items="listProps">
      <tr
        v-if="
          !(listProps.item.contractQty >= 2) ||
          listProps.item[expandColumn].contracts
        "
        :active="listProps.selected"
      >
        <template v-for="(header, idx) in headers">
          <!-- Se tiver um slot para esse header, usa ele -->
          <td :class="header.itemClass || 'text-xs-center'">
            <template v-if="header.value === 'selected'">
              <v-checkbox
                :input-value="listProps.selected"
                :ref="`checkbox-${listProps.index}`"
                @click.stop="toggleRow(listProps)"
                :disabled="
                  !ignorePrevious &&
                  !(
                    listProps.index === lastSelectedIndex + 1 ||
                    listProps.index === lastSelectedIndex
                  )
                "
                v-if="!listProps.item.totalRow"
                hide-details
              ></v-checkbox>
            </template>
            <template v-else-if="header.value === expandColumn">
              <!-- Shows a tooltip only on first slip of unified -->
              <v-tooltip
                right
                v-if="
                  listProps.item.contractQty >= 2 &&
                  listProps.item[expandColumn].contracts
                "
              >
                <template #activator="{ on }">
                  <span v-on="on">
                    <v-btn-toggle v-model="listProps.expanded" active-class="">
                      <v-btn value="true" icon class="financial--color">
                        <v-icon>bexsicon-info</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </span>
                </template>
                <span>Parcela Unificada</span>
              </v-tooltip>
            </template>
            <template v-else-if="$scopedSlots[`col-${header.value}`]">
              <slot
                :name="`col-${header.value}`"
                v-bind="listProps"
                :header="header"
              ></slot>
            </template>
            <template v-else>
              {{ showValue(listProps.item, header.value, header.formatter) }}
            </template>
          </td>
        </template>
      </tr>
    </template>
    <template #footer v-if="$scopedSlots[`listFooter`]">
      <slot name="listFooter" v-bind="{ currentSelected, selectedTotal }">
      </slot>
    </template>

    <!-- Slot para expandir detalhes -->
    <template v-if="slipDetailsHeaders.length" #expand="positionProps">
      <slot name="expand" v-bind="positionProps">
        <v-card
          v-if="positionProps.item.contractQty >= 1"
          raised
          class="expanded-area"
        >
          <v-data-table
            v-if="!!positionProps.item[expandColumn].contracts"
            :headers="slipDetailsHeaders"
            :items="positionProps.item[expandColumn].contracts"
            hide-actions
            sort-icon="bexsicon-down"
            :pagination.sync="expandColumnPagination"
            item-key="contractId"
            expand
          >
            <template #items="expandProps">
              <td
                @click.stop="expandProps.expanded = !expandProps.expanded"
                v-for="(header, i) in slipDetailsHeaders"
                :key="i"
                :class="header.itemClass || 'text-xs-center'"
              >
                <template v-if="$scopedSlots[`expandcol-${header.value}`]">
                  <slot
                    :name="`expandcol-${header.value}`"
                    v-bind="expandProps"
                    :header="header"
                  ></slot>
                </template>
                <template v-else>
                  {{
                    showValue(expandProps.item, header.value, header.formatter)
                  }}
                </template>
              </td>
            </template>
            <template #expand="contractProps">
              <div>
                <div
                  dense
                  class="w-100"
                  style="width: 100%; display: inline-table"
                >
                  <thead>
                    <tr>
                      <template v-for="cardHeader in headersWithoutExtra">
                        <th
                          v-if="cardHeader.value === expandColumn"
                          class="text-xs-left"
                        >
                          #
                        </th>
                        <th v-else>
                          {{ cardHeader.text }}
                        </th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(slip, slipIdx) in unitFinancialPositions[
                        contractProps.item.contractId
                      ][mode].filter(
                        (item) =>
                          item.documentCode === positionProps.item.documentCode
                      )"
                      :key="slipIdx"
                    >
                      <template v-for="cardHeader in headersWithoutExtra">
                        <td
                          v-if="cardHeader.value === expandColumn"
                          class="text-xs-left"
                        >
                          {{ slipIdx + 1 }}
                        </td>
                        <td
                          v-else
                          :class="cardHeader.itemClass || 'text-xs-center'"
                        >
                          {{
                            showValue(
                              slip,
                              cardHeader.value,
                              cardHeader.formatter,
                              expandColumn
                            )
                          }}
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </div>
              </div>
            </template>
          </v-data-table>
          <slot name="expand-footer" v-bind="positionProps.item[expandColumn]">
          </slot>
        </v-card>
      </slot>
    </template>
  </v-data-table>
</template>

<script>
import ScreenLoading from '@/components/loaders/ScreenLoading';
export default {
  name: 'FinancialTable',
  components: {
    ScreenLoading,
  },
  props: {
    headers: { type: Array, required: true },
    slipDetailsHeaders: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    unitFinancialPositions: { type: Object, required: true },
    mode: { type: String, required: true },
    loading: { type: Boolean, default: false },
    noDataText: { type: String, default: 'Nenhum dado encontrado' },
    hideActions: { type: Boolean, default: true },
    sortIcon: { type: String, default: 'bexsicon-down' },
    expandColumn: {
      type: String,
      default: 'slipsUnion',
    },

    /*  Selection fields  */
    selectable: { type: Boolean, default: false },
    ignorePrevious: {
      type: Boolean,
    },
    totalKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentSelected: [],
      lastSelectedIndex: -1,
      pagination: {
        sortBy: 'expirationDate', // campo inicial para ordenação
        descending: false, // true = decrescente, false = crescente
      },
      expandColumnPagination: {
        sortBy: 'contractId', // campo inicial para ordenação
        descending: false, // true = decrescente, false = crescente
      },
    };
  },
  computed: {
    headersWithoutExtra() {
      return this.headers.filter((h) => h.value != 'selected');
    },
    allSelected() {
      return this.currentSelected.length === this.items.length;
    },
    selectedTotal() {
      const total = this.currentSelected.reduce((sum, selected) => {
        const value = !(selected.contractQty >= 2)
          ? selected[this.totalKey]
          : selected[this.expandColumn].total;
        return sum + value;
      }, 0);

      return this.formatMoney(total);
    },
  },
  methods: {
    showValue(item, headerValue, formatter, extraInfo) {
      let collumnValue = item[headerValue];
      if (formatter) {
        return formatter(collumnValue, item, headerValue, extraInfo);
      } else {
        return item[headerValue];
      }
    },
    updateSelected(v) {
      this.currentSelected = v;
      this.$emit('updateSelected', v);
    },
    toggleRow(listPropsItem) {
      if (this.ignorePrevious) {
        listPropsItem.selected = !listPropsItem.selected;
      } else {
        listPropsItem.index === this.lastSelectedIndex + 1 ||
        listPropsItem.index === this.lastSelectedIndex
          ? (() => {
              listPropsItem.selected = !listPropsItem.selected;
              this.lastSelectedIndex =
                listPropsItem.index === this.lastSelectedIndex
                  ? listPropsItem.index - 1
                  : listPropsItem.index;
            })()
          : null;
      }
    },
    toggleAll() {
      this.lastSelectedIndex = this.items.length - 1;
      if (this.currentSelected.length === this.items.length) {
        this.currentSelected = [];
        this.lastSelectedIndex = -1;
      } else {
        this.currentSelected = this.items.slice();
      }
      this.$emit('updateSelected', this.currentSelected);
    },
  },
};
</script>

<style lang="scss" scoped>
>>> .financial-table {
  /* Linha 'top' quando logo abaixo vem uma expandida com conteúdo */

  tr:has(+ tr.v-datatable__expand-row) {
    cursor: pointer;
  }
  tr:has(+ tr.v-datatable__expand-row td.v-datatable__expand-col--expanded) {
    border: 1px solid #b3b1b1;
    border-bottom: none;

    .expanded-area-icon .v-icon {
      rotate: 90deg;
    }
  }

  /* Linha expandida */
  tr.v-datatable__expand-row:has(td.v-datatable__expand-col--expanded) {
    border: 1px solid #b3b1b1;
    border-top: none;

    .v-datatable__expand-col--expanded {
      border: none;
    }
  }

  .expanded-area {
    background: #eafbe4; /* Um verde bem claro de fundo, combinando com #4bb629 */
    border-left: 4px solid #4bb629; /* Destaque forte à esquerda */
    border-radius: 4px;
    margin: 8px 0;
    padding: 16px;
    transition: background 0.3s, border 0.3s;
  }

  .v-btn-toggle {
    box-shadow: none !important;
    background-color: inherit !important;

    .v-btn--active {
      &::before {
        background-color: transparent !important;
      }
      &:hover::before {
        background-color: black !important;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// ./src/components/financial/FinancialTable.vue